import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getUsers,
  createUser,
  updateUser,
  resetUserPassword,
  resetZweiterFaktor,
  deleteUser,
  inviteUser,
  resendInvite,
  type User,
  type CreateUserDto,
  type UpdateUserDto,
  type GlobaleRolle,
} from '@/api/users.api';
import { fetchRoles, assignUserRoles, type Role } from '@/api/roles.api';
import { listTrees } from '@/api/trees.api';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useAdminAuth } from '@/store/useAdminAuth';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { auswirkungFuerKonto, fetchTarife } from '@/api/plans.api';
import { Tarifwirkung } from '@/components/Tarifwirkung';

// ─── Rollen-Labels ──────────────────────────────────────────────────────────

// Die globale Rolle sagt nur, ob jemand die Installation betreibt. Was er in
// einem Stammbaum darf, steht in seiner Mitgliedschaft.
//
// 'viewer' ist die alte Bezeichnung: die Mandanten-Migration hat alle Konten
// auf 'user' gesetzt, sie kann in Altbeständen aber noch auftauchen. Ohne
// Eintrag stand hier der rohe Wert ohne Beschriftung.
const ROLE_LABELS: Record<string, string> = {
  admin: 'Betreiber',
  user: 'Nutzer',
  viewer: 'Nutzer',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  user: 'bg-slate-100 text-slate-600',
  viewer: 'bg-slate-100 text-slate-600',
};

// ─── User erstellen Modal ───────────────────────────────────────────────────

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'manual' | 'invite'>('manual');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<GlobaleRolle>('user');

  const createMutation = useMutation({
    mutationFn: (dto: CreateUserDto) => createUser(dto),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${user.email} wurde angelegt.`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Benutzer konnte nicht erstellt werden.'),
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) => inviteUser(email, role),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Einladung an ${user.email} gesendet.`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Einladung fehlgeschlagen.'),
  });

  const isPending = createMutation.isPending || inviteMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'manual') {
      createMutation.mutate({ email, password, role });
    } else {
      inviteMutation.mutate({ email, role });
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h2 className="mb-4 text-lg font-semibold text-slate-900">Neuen Benutzer</h2>

      {/* Mode-Auswahl */}
      <div className="mb-5 flex rounded-md border border-slate-200 bg-slate-50 p-1">
        <button
          type="button"
          onClick={() => setMode('manual')}
          className={`flex-1 rounded py-1.5 text-sm font-medium transition-colors ${mode === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Manuell anlegen
        </button>
        <button
          type="button"
          onClick={() => setMode('invite')}
          className={`flex-1 rounded py-1.5 text-sm font-medium transition-colors ${mode === 'invite' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Per E-Mail einladen
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="newUserEmail" className="mb-1 block text-sm font-medium text-slate-700">E-Mail</label>
          <input
            id="newUserEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {mode === 'manual' && (
          <div>
            <label htmlFor="newUserPassword" className="mb-1 block text-sm font-medium text-slate-700">
              Passwort (min. 8 Zeichen)
            </label>
            <input
              id="newUserPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
        )}

        <div>
          <label htmlFor="newUserRole" className="mb-1 block text-sm font-medium text-slate-700">Rolle</label>
          <select
            id="newUserRole"
            value={role}
            onChange={(e) => setRole(e.target.value as GlobaleRolle)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="user">Nutzer</option>
            <option value="admin">Betreiber</option>
          </select>
        </div>

        <p className="text-xs text-slate-400">
          {mode === 'manual'
            ? 'Der Benutzer muss sein Passwort beim ersten Login ändern.'
            : 'Die Person erhält eine E-Mail mit einem Einmal-Link zum Festlegen ihres Passworts (gültig 7 Tage).'}
        </p>

        <ModalActions
          onClose={onClose}
          submitLabel={isPending ? (mode === 'invite' ? 'Sende...' : 'Wird erstellt...') : (mode === 'invite' ? 'Einladung senden' : 'Erstellen')}
          isPending={isPending}
        />
      </form>
    </ModalOverlay>
  );
}

// ─── User bearbeiten Modal ──────────────────────────────────────────────────

function EditUserModal({ user, onClose }: { user: User; onClose: () => void }) {
  const queryClient = useQueryClient();
  const currentUserId = useAdminAuth((s) => s.user?.id);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<GlobaleRolle>(user.role === 'viewer' ? 'user' : user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  // Leer = keine Ausnahme, es gilt der Tarif.
  const [kontingent, setKontingent] = useState(user.maxTrees === null ? '' : String(user.maxTrees));
  // '' = kein eigener Tarif, es gilt die Vorgabe der Installation.
  const [planId, setPlanId] = useState(user.planId ?? '');

  const isSelf = user.id === currentUserId;

  const { data: tarife } = useQuery({ queryKey: ['admin', 'plans'], queryFn: fetchTarife });

  // Was der GEWÄHLTE Tarif für dieses Konto bedeutet. Läuft bei jeder Auswahl
  // neu, aber nur, wenn sie sich vom gespeicherten Stand unterscheidet – sonst
  // stünde beim bloßen Öffnen des Dialogs eine Warnung über den Ist-Zustand.
  const tarifGeaendert = (planId || null) !== (user.planId ?? null);
  const { data: wirkung, isFetching: wirkungLaeuft } = useQuery({
    queryKey: ['admin', 'plans', 'auswirkung', user.id, planId],
    queryFn: () => auswirkungFuerKonto(user.id, planId),
    enabled: tarifGeaendert,
    staleTime: 0,
  });

  // Welcher Tarif greift gerade – der gewählte, sonst die Vorgabe. Nur für den
  // Platzhalter im Kontingent-Feld, damit dort die echte Zahl steht.
  const tarifDesKontos =
    (planId === ''
      ? tarife?.data.find((t) => t.isDefault)
      : tarife?.data.find((t) => t.id === planId)) ?? null;

  const mutation = useMutation({
    mutationFn: (dto: UpdateUserDto) => updateUser(user.id, dto),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${updated.email} wurde aktualisiert.`);
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Benutzer konnte nicht bearbeitet werden.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const changes: UpdateUserDto = {};
    if (email !== user.email) changes.email = email;
    if (role !== user.role) changes.role = role;
    if (isActive !== user.isActive) changes.isActive = isActive;

    const neuesKontingent = kontingent.trim() === '' ? null : Number(kontingent);
    if (neuesKontingent !== null && (!Number.isInteger(neuesKontingent) || neuesKontingent < 0)) {
      toast.error('Kontingent muss eine Zahl ab 0 sein.');
      return;
    }
    if (neuesKontingent !== user.maxTrees) changes.maxTrees = neuesKontingent;

    const neuerTarif = planId === '' ? null : planId;
    if (neuerTarif !== user.planId) changes.planId = neuerTarif;

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    mutation.mutate(changes);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h2 className="mb-4 text-lg font-semibold text-slate-900">Benutzer bearbeiten</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="editUserEmail" className="mb-1 block text-sm font-medium text-slate-700">
            E-Mail
          </label>
          <input
            id="editUserEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label htmlFor="editUserRole" className="mb-1 block text-sm font-medium text-slate-700">
            Rolle
          </label>
          <select
            id="editUserRole"
            value={role}
            onChange={(e) => setRole(e.target.value as GlobaleRolle)}
            disabled={isSelf}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-400"
          >
            <option value="user">Nutzer</option>
            <option value="admin">Betreiber</option>
          </select>
          {isSelf && (
            <p className="mt-1 text-xs text-slate-400">Du kannst deine eigene Rolle nicht ändern.</p>
          )}
        </div>

        <div>
          <label htmlFor="editUserPlan" className="mb-1 block text-sm font-medium text-slate-700">
            Tarif
          </label>
          <select
            id="editUserPlan"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              Vorgabe der Installation
              {tarife?.data.find((t) => t.isDefault)
                ? ` (${tarife.data.find((t) => t.isDefault)!.name})`
                : ''}
            </option>
            {(tarife?.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-400">
            Bestimmt Funktionsumfang, Speicher je Stammbaum und die Zahl eigener
            Stammbäume. Für einen Stammbaum gilt immer der Tarif seines
            Besitzers – nicht der des gerade Arbeitenden.
          </p>

          {/* Was der gewählte Tarif brechen würde – vor dem Speichern. Ohne
              diese Zeile erfährt der Betreiber es durch den Anruf des Kunden. */}
          <Tarifwirkung auswirkung={wirkung} laeuft={wirkungLaeuft} />
        </div>

        <div>
          <label htmlFor="editUserQuota" className="mb-1 block text-sm font-medium text-slate-700">
            Eigene Stammbäume (Ausnahme)
          </label>
          <input
            id="editUserQuota"
            type="number"
            min={0}
            max={1000}
            value={kontingent}
            onChange={(e) => setKontingent(e.target.value)}
            placeholder={
              tarifDesKontos ? `laut Tarif: ${tarifDesKontos.maxTrees}` : 'laut Tarif'
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <p className="mt-1 text-xs text-slate-400">
            Übersteuert den Tarif für dieses eine Konto. Leer lassen, damit der
            Tarif gilt. Aktuell besitzt es {user.ownedTrees}.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isSelf}
              className="peer sr-only"
            />
            <div className="h-5 w-9 rounded-full bg-slate-300 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-disabled:opacity-50" />
          </label>
          <span className="text-sm text-slate-700">
            {isActive ? 'Aktiv' : 'Deaktiviert'}
          </span>
          {isSelf && (
            <span className="text-xs text-slate-400">(nicht änderbar)</span>
          )}
        </div>

        <ModalActions onClose={onClose} submitLabel={mutation.isPending ? 'Wird gespeichert...' : 'Speichern'} isPending={mutation.isPending} />
      </form>
    </ModalOverlay>
  );
}

// ─── Passwort zurücksetzen Modal ────────────────────────────────────────────

function ResetPasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [newPassword, setNewPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => resetUserPassword(user.id, newPassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Passwort für ${user.email} wurde zurückgesetzt.`);
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Passwort konnte nicht zurückgesetzt werden.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h2 className="mb-4 text-lg font-semibold text-slate-900">Passwort zurücksetzen</h2>

      <div className="mb-4 rounded-md bg-amber-50 p-3">
        <p className="text-sm text-amber-700">
          Das Passwort von <strong>{user.email}</strong> wird zurückgesetzt.
          Der Benutzer muss es beim nächsten Login ändern.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="resetPassword" className="mb-1 block text-sm font-medium text-slate-700">
            Neues Passwort (min. 8 Zeichen)
          </label>
          <input
            id="resetPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            autoFocus
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <ModalActions onClose={onClose} submitLabel={mutation.isPending ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'} isPending={mutation.isPending} variant="warning" />
      </form>
    </ModalOverlay>
  );
}

// ─── Zwei-Faktor zurücksetzen ───────────────────────────────────────────────
//
// Der Notausgang für „Telefon verloren, Ersatzcodes auch weg". Ohne ihn bliebe
// nur, das Konto zu löschen und neu anzulegen.
//
// Der Dialog sagt ohne Beschönigung, was gleich passiert, und verlangt einen
// Grund im Klartext. Der steht danach im Protokoll UND in der E-Mail an den
// Betroffenen – er ist das Einzige, was diesen Eingriff hinterher erklärt.

function ZweiFaktorResetModal({ user, onClose }: { user: User; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [grund, setGrund] = useState('');

  const mutation = useMutation({
    mutationFn: () => resetZweiterFaktor(user.id, grund),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Zwei-Faktor-Anmeldung für ${user.email} wurde zurückgesetzt.`);
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Zwei-Faktor-Anmeldung konnte nicht zurückgesetzt werden.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h2 className="mb-4 text-lg font-semibold text-slate-900">Zwei-Faktor zurücksetzen</h2>

      <div className="mb-4 rounded-md bg-red-50 p-3">
        <p className="text-sm text-red-700">
          Für <strong>{user.email}</strong> werden entfernt:
        </p>
        <ul className="mt-2 list-disc pl-5 text-sm text-red-700">
          {user.totpEnabled && <li>die Authenticator-App</li>}
          <li>alle Ersatzcodes</li>
          {user.passkeys > 0 && (
            <li>
              {user.passkeys === 1 ? 'ein Passkey' : `alle ${user.passkeys} Passkeys`}
            </li>
          )}
          <li>alle laufenden Sitzungen dieses Kontos</li>
        </ul>
        <p className="mt-2 text-sm text-red-700">
          Danach genügt das Passwort allein zum Anmelden. Der Kontoinhaber wird per E-Mail
          benachrichtigt.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="zweiFaktorGrund" className="mb-1 block text-sm font-medium text-slate-700">
            Grund (mind. 10 Zeichen)
          </label>
          <textarea
            id="zweiFaktorGrund"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            required
            minLength={10}
            maxLength={500}
            rows={3}
            autoFocus
            placeholder="z. B. Telefon verloren, Identität am 12.08. telefonisch geprüft"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <p className="mt-1 text-xs text-slate-500">
            Steht im Protokoll und in der E-Mail an den Kontoinhaber.
          </p>
        </div>

        <ModalActions
          onClose={onClose}
          submitLabel={mutation.isPending ? 'Wird zurückgesetzt...' : 'Zwei-Faktor zurücksetzen'}
          isPending={mutation.isPending}
          variant="warning"
        />
      </form>
    </ModalOverlay>
  );
}

// ─── Gemeinsame Modal-Komponenten ───────────────────────────────────────────

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  onClose,
  submitLabel,
  isPending,
  variant = 'primary',
}: {
  onClose: () => void;
  submitLabel: string;
  isPending: boolean;
  variant?: 'primary' | 'warning' | 'danger';
}) {
  const colors = {
    primary: 'bg-blue-600 hover:bg-blue-700',
    warning: 'bg-amber-600 hover:bg-amber-700',
    danger: 'bg-red-600 hover:bg-red-700',
  };

  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Abbrechen
      </button>
      <button
        type="submit"
        disabled={isPending}
        className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${colors[variant]}`}
      >
        {submitLabel}
      </button>
    </div>
  );
}

// ─── Rollen zuweisen Modal ──────────────────────────────────────────────────

function ManageRolesModal({ user, onClose }: { user: User; onClose: () => void }) {
  const queryClient = useQueryClient();

  // Rechte gelten JE STAMMBAUM: erst Baum wählen, dann dessen Rollen.
  const { data: trees, isLoading: loadingTrees } = useQuery({
    queryKey: ['trees', 'accessible'],
    queryFn: listTrees,
  });

  const [treeId, setTreeId] = useState<string>('');
  useEffect(() => {
    if (!treeId && trees && trees.length > 0) setTreeId(trees[0].id);
  }, [trees, treeId]);

  // Beim Baumwechsel die dort bereits zugewiesenen Rollen vorbelegen
  const assignedForTree = useMemo(
    () => user.treeRoles?.find((tr) => tr.treeId === treeId)?.roleIds ?? [],
    [user.treeRoles, treeId],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected(new Set(assignedForTree));
  }, [assignedForTree]);

  const { data: roles, isLoading: loadingRoles } = useQuery<Role[]>({
    queryKey: QUERY_KEYS.roles.all,
    queryFn: fetchRoles,
  });

  const assignableRoles = useMemo(
    () => (roles ?? []).filter((r) => !r.isSystem),
    [roles],
  );

  const toggle = (roleId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: () => assignUserRoles(user.id, treeId, Array.from(selected)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.all });
      toast.success(`Rollen für ${user.email} in diesem Stammbaum aktualisiert.`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Zuweisung fehlgeschlagen.'),
  });

  return (
    <ModalOverlay onClose={onClose}>
      <h2 className="mb-1 text-lg font-semibold text-slate-900">Rollen zuweisen</h2>
      <p className="mb-4 text-sm text-slate-500">
        Rechte gelten je Stammbaum. Wähle den Stammbaum und die dort geltenden Rollen für{' '}
        <span className="font-medium text-slate-700">{user.email}</span>. Ohne Rolle hat der
        Nutzer in diesem Stammbaum keinen Zugriff.
      </p>

      {user.role === 'admin' && (
        <div className="mb-4 rounded-md bg-purple-50 p-3 text-sm text-purple-700">
          Admins umgehen das Rollensystem (Superuser-Bypass) und sehen alle Stammbäume.
          Zuweisungen haben hier keine praktische Auswirkung.
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="role-tree" className="mb-1 block text-sm font-medium text-slate-700">
          Stammbaum
        </label>
        <select
          id="role-tree"
          value={treeId}
          onChange={(e) => setTreeId(e.target.value)}
          disabled={loadingTrees}
          className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
        >
          {(trees ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {loadingRoles && (
        <div className="flex justify-center py-6">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        </div>
      )}

      {assignableRoles && assignableRoles.length === 0 && !loadingRoles && (
        <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
          Noch keine Rollen definiert.
          <Link to="/rollen" className="ml-1 text-blue-600 hover:underline">
            Rolle anlegen
          </Link>
        </div>
      )}

      {assignableRoles && assignableRoles.length > 0 && (
        <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
          {assignableRoles.map((r) => (
            <label
              key={r.id}
              className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
                className="mt-0.5 rounded border-slate-300"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800">{r.name}</p>
                {r.description && (
                  <p className="text-xs text-slate-500">{r.description}</p>
                )}
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {r.permissions.length} Berechtigungen
                </p>
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
        </button>
      </div>
    </ModalOverlay>
  );
}

// ─── User-Aktionen Dropdown ─────────────────────────────────────────────────
//
// Das Menü hängt an document.body, nicht im Dokumentfluss der Zeile. Grund:
// die Tabelle liegt in einem Kasten mit `overflow-x-auto` (sonst schieben sich
// die Spalten unter 900 px ineinander), und ein solcher Kasten schneidet alles
// ab, was über seinen Rand hinausragt.
//
// Die Falle dabei: `overflow-x: auto` zwingt die Y-Achse von `visible` auf
// `auto` mit. Das Menü wurde deshalb nicht nur rechts abgeschnitten, sondern
// auch unten – wer auf die drei Punkte klickte, sah nur den oberen Teil.
//
// Feste Position statt absoluter: `fixed` misst am Ansichtsfenster und nicht
// am nächsten positionierten Vorfahren, entkommt also jedem Schneider. Der
// Preis ist, dass die Position beim Rollen nicht mitwandert – deshalb schließt
// das Menü bei Roll- und Größenänderungen.

/** Wo das Menü hin soll, gemessen am Knopf. */
function menueLage(knopf: HTMLElement): { top: number; left: number } {
  const r = knopf.getBoundingClientRect();
  const BREITE = 192; // w-48
  const HOEHE = 260; // grobe Höhe; nur für die Entscheidung oben/unten

  // Rechtsbündig am Knopf, aber nie über den Fensterrand hinaus.
  const left = Math.max(8, Math.min(r.right - BREITE, window.innerWidth - BREITE - 8));

  // Nach oben klappen, wenn unten kein Platz mehr ist.
  const platzUnten = window.innerHeight - r.bottom;
  const top = platzUnten < HOEHE && r.top > HOEHE ? r.top - HOEHE - 4 : r.bottom + 4;

  return { top, left };
}

function UserActions({
  user,
  isSelf,
  onEdit,
  onManageRoles,
  onResetPassword,
  onResetZweiterFaktor,
  onResendInvite,
  onDelete,
}: {
  user: User;
  isSelf: boolean;
  onEdit: () => void;
  onManageRoles: () => void;
  onResetPassword: () => void;
  onResetZweiterFaktor: () => void;
  onResendInvite: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [lage, setLage] = useState<{ top: number; left: number } | null>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);

  // Ein Menü mit fester Position wandert beim Rollen nicht mit. Statt es
  // laufend nachzuführen, schließt es – das ist ehrlicher als ein Menü, das
  // neben seinem Knopf steht.
  useEffect(() => {
    if (!open) return;
    const schliessen = () => setOpen(false);
    window.addEventListener('scroll', schliessen, true);
    window.addEventListener('resize', schliessen);
    return () => {
      window.removeEventListener('scroll', schliessen, true);
      window.removeEventListener('resize', schliessen);
    };
  }, [open]);

  const umschalten = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (knopfRef.current) setLage(menueLage(knopfRef.current));
    setOpen(true);
  };

  return (
    <div>
      <button
        ref={knopfRef}
        onClick={umschalten}
        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        title="Aktionen"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
        </svg>
      </button>

      {open && lage && createPortal(
        <>
          {/* Backdrop zum Schliessen */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          <div
            className="fixed z-50 w-48 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            style={{ top: lage.top, left: lage.left }}
          >
            <button
              onClick={() => { setOpen(false); onEdit(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
              Bearbeiten
            </button>

            <button
              onClick={() => { setOpen(false); onManageRoles(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
              </svg>
              Rollen zuweisen
            </button>

            <button
              onClick={() => { setOpen(false); onResetPassword(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
              </svg>
              Passwort zurücksetzen
            </button>

            {(user.totpEnabled || user.passkeys > 0) && (
              <button
                onClick={() => { setOpen(false); onResetZweiterFaktor(); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
                Zwei-Faktor zurücksetzen
              </button>
            )}

            {!user.isActive && (
              <button
                onClick={() => { setOpen(false); onResendInvite(); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                Einladung erneut senden
              </button>
            )}

            {!isSelf && (
              <>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={() => { setOpen(false); onDelete(); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                  Löschen
                </button>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

// ─── Tarif einer Zeile ──────────────────────────────────────────────────────
//
// Ohne eigenen Tarif gilt die Vorgabe. Die wird blass dazugeschrieben, statt
// ein leeres Feld zu zeigen – sonst müsste man in den Einstellungen nachsehen,
// was für dieses Konto eigentlich gilt.

function TarifZelle({ nutzer, vorgabe }: { nutzer: User; vorgabe: string | null }) {
  if (nutzer.plan) {
    return (
      <span className="inline-block rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
        {nutzer.plan.name}
      </span>
    );
  }
  return (
    <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
      {vorgabe ? `${vorgabe} (Vorgabe)` : 'kein Tarif'}
    </span>
  );
}

// ─── AdminUsersPage ─────────────────────────────────────────────────────────

export function AdminUsersPage() {
  const currentUserId = useAdminAuth((s) => s.user?.id);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [zweiFaktorUser, setZweiFaktorUser] = useState<User | null>(null);
  const [manageRolesUser, setManageRolesUser] = useState<User | null>(null);
  const [suche, setSuche] = useState('');

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const { data: tarife } = useQuery({ queryKey: ['admin', 'plans'], queryFn: fetchTarife });
  const vorgabeTarif = tarife?.data.find((t) => t.isDefault) ?? null;

  // Gesucht wird über E-Mail, Tarif und die Namen der zugewiesenen Rollen –
  // also über das, was in der Zeile steht. Im Browser statt auf dem Server:
  // die Liste ist ohnehin vollständig geladen, und ein Rundgang zum Server je
  // Tastendruck wäre langsamer als das Filtern selbst.
  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q || !users) return users ?? [];
    return users.filter((u) => {
      const tarif = u.plan?.name ?? vorgabeTarif?.name ?? '';
      const rollen = (u.treeRoles ?? [])
        .flatMap((tr) => [tr.treeName, ...tr.roleNames])
        .join(' ');
      return `${u.email} ${tarif} ${rollen}`.toLowerCase().includes(q);
    });
  }, [users, suche, vorgabeTarif]);

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Benutzer gelöscht.');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Benutzer konnte nicht gelöscht werden.');
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (userId: string) => resendInvite(userId),
    onSuccess: () => toast.success('Einladung erneut gesendet.'),
    onError: (err: Error) => toast.error(err.message || 'Fehler beim erneuten Senden.'),
  });

  const handleDelete = async (user: User) => {
    const ok = await confirm({
      title: 'Benutzer löschen?',
      message: (
        <>
          "<strong>{user.email}</strong>" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </>
      ),
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(user.id);
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Topbar */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2.5 shadow-sm sm:h-14 sm:flex-nowrap sm:py-0">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Benutzerverwaltung</h1>
        </div>

        <input
          type="search"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Suchen: E-Mail, Tarif, Rolle"
          aria-label="Konten durchsuchen"
          className="order-last w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:order-none sm:ml-auto sm:mr-3 sm:w-64"
        />

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          <span className="hidden sm:inline">Benutzer anlegen</span>
          <span className="sm:hidden">Anlegen</span>
        </button>
      </header>

      {/* Inhalt */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-8">
        <div className="mx-auto w-full max-w-6xl">
          {isLoading && (
            <div className="flex justify-center py-12">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          )}

          {users && sichtbar.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="text-sm text-slate-500">
                {users.length === 0
                  ? 'Keine Benutzer vorhanden.'
                  : `Kein Konto passt zu „${suche}".`}
              </p>
            </div>
          )}

          {sichtbar.length > 0 && (
            <>
              {/* Desktop: Tabelle */}
              {/* Die Tabelle braucht mit Tarif-Spalte rund 900 px. Statt die
                  Spalten auf einem schmalen Bildschirm ineinanderzuschieben,
                  bekommt sie einen eigenen waagerechten Rollbalken – die Seite
                  selbst rollt nicht seitlich. Das Aktionen-Menü klappt nach
                  außen auf, deshalb liegt overflow-x-auto INNEN und nicht auf
                  demselben Element wie der Rahmen. */}
              <div className="hidden rounded-lg border border-slate-200 bg-white shadow-sm md:block">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[56rem]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">E-Mail</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Rolle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Tarif</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Zugewiesene Rollen</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">2FA</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Erstellt</th>
                      {/* Angeheftet: sonst liegt die Spalte bei schmalen
                          Fenstern außerhalb, und man muss erst seitlich rollen,
                          um überhaupt an das Menü zu kommen. */}
                      <th className="sticky right-0 bg-slate-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sichtbar.map((user) => {
                      const isSelf = user.id === currentUserId;
                      return (
                        <tr key={user.id} className={`group hover:bg-slate-50 ${!user.isActive ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">
                            {user.email}
                            {isSelf && (
                              <span className="ml-2 text-xs text-slate-400">(du)</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[user.role] ?? ROLE_COLORS.viewer}`}>
                              {ROLE_LABELS[user.role] ?? user.role}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <TarifZelle nutzer={user} vorgabe={vorgabeTarif?.name ?? null} />
                          </td>
                          <td className="px-4 py-3">
                            {user.treeRoles && user.treeRoles.length > 0 ? (
                              <div className="space-y-1">
                                {user.treeRoles.map((tr) => (
                                  <div key={tr.treeId} className="flex flex-wrap items-center gap-1">
                                    <span className="text-[11px] text-slate-500">{tr.treeName}:</span>
                                    {tr.roleNames.map((rn) => (
                                      <span
                                        key={rn}
                                        className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
                                      >
                                        {rn}
                                      </span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            ) : user.role === 'admin' ? (
                              <span className="text-xs text-slate-400">—</span>
                            ) : (
                              <span className="text-xs text-slate-400">Keine</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {!user.isActive ? (
                              <span className="inline-flex items-center gap-1 text-red-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                Deaktiviert
                              </span>
                            ) : user.mustChangePassword ? (
                              <span className="inline-flex items-center gap-1 text-amber-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                PW-Änderung nötig
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                Aktiv
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {/* Passkeys zählen mit: ein Konto ohne TOTP, aber mit
                                Passkey, ist NICHT ungeschützt. */}
                            {user.totpEnabled || user.passkeys > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                </svg>
                                {user.totpEnabled && user.passkeys > 0
                                  ? 'App + Passkey'
                                  : user.totpEnabled
                                    ? 'App'
                                    : user.passkeys === 1
                                      ? 'Passkey'
                                      : `${user.passkeys} Passkeys`}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">Aus</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {new Date(user.createdAt).toLocaleDateString('de-DE')}
                          </td>
                          <td className="sticky right-0 bg-white px-4 py-3 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)] group-hover:bg-slate-50">
                            <UserActions
                              user={user}
                              isSelf={isSelf}
                              onEdit={() => setEditUser(user)}
                              onManageRoles={() => setManageRolesUser(user)}
                              onResetPassword={() => setResetPasswordUser(user)}
                              onResetZweiterFaktor={() => setZweiFaktorUser(user)}
                              onResendInvite={() => resendInviteMutation.mutate(user.id)}
                              onDelete={() => handleDelete(user)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Mobile: Karten */}
              <div className="space-y-3 md:hidden">
                {sichtbar.map((user) => {
                  const isSelf = user.id === currentUserId;
                  return (
                    <div key={user.id} className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm ${!user.isActive ? 'opacity-50' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-900">
                            {user.email}
                            {isSelf && <span className="ml-1 text-xs text-slate-400">(du)</span>}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[user.role] ?? ROLE_COLORS.viewer}`}>
                              {ROLE_LABELS[user.role] ?? user.role}
                            </span>
                            <TarifZelle nutzer={user} vorgabe={vorgabeTarif?.name ?? null} />
                            {user.treeRoles?.map((tr) =>
                              tr.roleNames.map((rn) => (
                                <span
                                  key={`${tr.treeId}-${rn}`}
                                  className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
                                  title={tr.treeName}
                                >
                                  {tr.treeName}: {rn}
                                </span>
                              )),
                            )}
                            {!user.isActive ? (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                Deaktiviert
                              </span>
                            ) : user.mustChangePassword ? (
                              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                PW-Änderung
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                Aktiv
                              </span>
                            )}
                            {(user.totpEnabled || user.passkeys > 0) && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                2FA
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-slate-400">
                            Erstellt: {new Date(user.createdAt).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        <UserActions
                          user={user}
                          isSelf={isSelf}
                          onEdit={() => setEditUser(user)}
                          onManageRoles={() => setManageRolesUser(user)}
                          onResetPassword={() => setResetPasswordUser(user)}
                          onResetZweiterFaktor={() => setZweiFaktorUser(user)}
                          onResendInvite={() => resendInviteMutation.mutate(user.id)}
                          onDelete={() => handleDelete(user)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {showCreateModal && <CreateUserModal onClose={() => setShowCreateModal(false)} />}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} />}
      {resetPasswordUser && <ResetPasswordModal user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} />}
      {zweiFaktorUser && <ZweiFaktorResetModal user={zweiFaktorUser} onClose={() => setZweiFaktorUser(null)} />}
      {manageRolesUser && <ManageRolesModal user={manageRolesUser} onClose={() => setManageRolesUser(null)} />}
    </div>
  );
}
